import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Search, MoreHorizontal, Eye, Mail, Loader2, Building2,
} from "lucide-react";
import { venuesApi, resolveMediaUrl, type VenueResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const Route = createFileRoute("/admin/venues")({
  component: VenuesPage,
});

const availStyles: Record<string, string> = {
  Active:     "bg-success/15 text-success",
  Inactive:   "bg-muted text-muted-foreground",
  Pending:    "bg-gold/20 text-foreground",
};

const modStyles: Record<string, string> = {
  approved:  "bg-green-100 text-green-700",
  pending:   "bg-amber-100 text-amber-700",
  rejected:  "bg-red-100 text-red-700",
};

function VenuesPage() {
  const { hasPermission } = useAuth();
  const canModerate = hasPermission("venues.moderate") || hasPermission("venues.manage_all");

  const [venues, setVenues] = useState<VenueResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "available" | "unavailable">("all");

  useEffect(() => {
    setLoading(true);
    venuesApi.list({ pageSize: 100 })
      .then(res => setVenues(res.data))
      .catch(err => setError(err instanceof Error ? err.message : "Failed to load venues"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = venues.filter(v => {
    const matchQ = !q || v.name.toLowerCase().includes(q.toLowerCase()) || v.location.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all"
      || (filter === "available" && v.isAvailable)
      || (filter === "unavailable" && !v.isAvailable);
    return matchQ && matchF;
  });

  const toggleAvailability = (id: string, current: boolean) => {
    setVenues(vs => vs.map(v => v.id === id ? { ...v, isAvailable: !current } : v));
    toast.success(`Venue ${current ? "hidden" : "listed"}`);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Venues</h1>
        <p className="text-sm text-muted-foreground">View and manage every listing on the platform.</p>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card shadow-soft">
        <div className="flex flex-col gap-3 border-b border-border/60 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search venue or location…" className="pl-9" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(["all", "available", "unavailable"] as const).map(s => (
              <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className="capitalize">{s}</Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading venues…
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 className="mb-3 size-10 text-muted-foreground/30" />
            <p className="text-sm font-semibold text-muted-foreground">
              {venues.length === 0 ? "No venues registered yet" : "No venues match your filters"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium sm:px-5">Venue</th>
                  <th className="hidden px-4 py-3 font-medium sm:table-cell sm:px-5">Type</th>
                  <th className="hidden px-4 py-3 font-medium sm:table-cell sm:px-5">Base Price</th>
                  <th className="px-4 py-3 font-medium sm:px-5">Status</th>
                  <th className="px-4 py-3 font-medium sm:px-5">Moderation</th>
                  <th className="px-4 py-3 font-medium sm:px-5">Available</th>
                  {canModerate && <th className="px-4 py-3 sm:px-5"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.map((v) => (
                  <tr key={v.id} className="hover:bg-accent/30">
                    <td className="px-4 py-3 sm:px-5">
                      <div className="flex items-center gap-3">
                        <img
                          src={resolveMediaUrl(v.heroImage)}
                          alt=""
                          className="size-10 shrink-0 rounded-lg object-cover"
                        />
                        <div className="min-w-0">
                          <div className="truncate font-medium">{v.name}</div>
                          <div className="truncate text-xs text-muted-foreground">{v.location}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell sm:px-5 capitalize">{v.type}</td>
                    <td className="hidden px-4 py-3 font-semibold sm:table-cell sm:px-5">{formatNaira(v.basePrice)}</td>
                    <td className="px-4 py-3 sm:px-5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${availStyles[v.status] ?? "bg-muted text-muted-foreground"}`}>
                        {v.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 sm:px-5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${modStyles[(v.moderationStatus ?? "").toLowerCase()] ?? "bg-muted text-muted-foreground"}`}>
                        {(v.moderationStatus ?? "pending").toLowerCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 sm:px-5">
                      <Switch
                        checked={v.isAvailable}
                        disabled={!canModerate}
                        onCheckedChange={() => toggleAvailability(v.id, v.isAvailable)}
                      />
                    </td>
                    {canModerate && (
                      <td className="px-4 py-3 sm:px-5">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="size-8"><MoreHorizontal className="size-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to="/venues/$venueId" params={{ venueId: String(v.id) }}>
                                <Eye className="mr-2 size-4" />View listing
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Mail className="mr-2 size-4" />Contact host
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
